import { BYTES_GIGABYTE, formatBytes } from '@air/utilities';

import {
  type CreditUsageCategory,
  type CreditUsageState,
  type CreditUsageStats,
} from '~/components/CreditUsageMeter/types';

export const CREDIT_USAGE_CATEGORIES = [
  'generative',
  'smartStorage',
  'storage',
] as const satisfies CreditUsageCategory[];

export const CREDIT_USAGE_CATEGORY_COLOR_MAP = {
  storage: 'bg-sky-5',
  smartStorage: 'bg-sky-7',
  generative: 'bg-sky-9',
} as const satisfies Record<CreditUsageCategory, string>;

const MIN_CREDIT_VALUE_FOR_LESS_THAN_INDICATOR = 0.1;
const MIN_CREDIT_VALUE_FOR_NO_DECIMALS = 10;
const MIN_CREDIT_VALUE_FOR_COMPACT_FORMAT = 10_000;

export const formatCreditUsage = (
  usage: number,
  options?: { noDecimals?: boolean; useLessThanIndicatorForSmallValues?: boolean },
) => {
  const formatOptions: Intl.NumberFormatOptions = {
    roundingMode: 'halfCeil',
  };

  if (options?.useLessThanIndicatorForSmallValues && usage > 0 && usage < MIN_CREDIT_VALUE_FOR_LESS_THAN_INDICATOR) {
    return `<.1`;
  }

  if (usage < MIN_CREDIT_VALUE_FOR_NO_DECIMALS && !options?.noDecimals) {
    formatOptions.minimumFractionDigits = 1;
    formatOptions.maximumFractionDigits = 1;
    formatOptions.roundingMode = 'ceil';
  } else {
    formatOptions.minimumFractionDigits = 0;
    formatOptions.maximumFractionDigits = 0;
  }

  if (usage >= MIN_CREDIT_VALUE_FOR_COMPACT_FORMAT) {
    formatOptions.notation = 'compact';
    formatOptions.compactDisplay = 'short';
  }

  return new Intl.NumberFormat('en-US', formatOptions).format(usage);
};

export const formatStorageUsage = (gb: number) => {
  return formatBytes(gb * BYTES_GIGABYTE, { numberOfDecimals: 1 });
};

export const getPercentageUsed = (used: number, allocated: number | null) => {
  if (!allocated) {
    return 0;
  }

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  return (used / allocated) * 100;
};

export const getCreditUsageState = ({
  allocated,
  used,
}: Pick<CreditUsageStats, 'allocated' | 'used'>): CreditUsageState => {
  const percentage = getPercentageUsed(used.total, allocated ?? 0);

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  if (percentage >= 100) {
    return 'depleted';
  }

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  if (percentage >= 90) {
    return '90%';
  }

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  if (percentage >= 75) {
    return '75%';
  }

  // biome-ignore lint/style/noMagicNumbers: percentage calculation
  if (percentage >= 50) {
    return '50%';
  }

  return 'default';
};
